import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Check, X, Minus, Camera, Save, Send, AlertCircle } from 'lucide-react';
import { useInspection, useInspectionResponses, useInspectionTemplateSections, useInspectionTemplateItems } from '@/hooks/useInspections';
import { supabase } from '@/integrations/supabase/client';
import { getSignedStorageUrl } from '@/utils/storageUtils';
import { toast } from 'sonner';

interface RunInspectionProps {
  inspectionId: string;
  onBack: () => void;
  onSubmit: () => void;
}

interface ResponseValue {
  value: string | null;
  notes: string;
  photoUrls: string[];
}

export const RunInspection: React.FC<RunInspectionProps> = ({ inspectionId, onBack, onSubmit }) => {
  const {
    inspection,
    isLoading: inspectionLoading,
    isError: inspectionError,
    error,
    updateInspection,
  } = useInspection(inspectionId);
  const { responses, createResponse, updateResponse } = useInspectionResponses(inspectionId);
  const { sections } = useInspectionTemplateSections(inspection?.template_id || '');
  const { items } = useInspectionTemplateItems(inspection?.template_id || '');

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [localResponses, setLocalResponses] = useState<Record<string, ResponseValue>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize local responses from saved responses
  useEffect(() => {
    if (responses) {
      const initial: Record<string, ResponseValue> = {};
      responses.forEach(r => {
        initial[r.template_item_id] = {
          value: r.response_value,
          notes: r.notes || '',
          photoUrls: r.photo_urls || []
        };
      });
      setLocalResponses(initial);
    }
  }, [responses]);

  const sortedSections = sections?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) || [];
  const currentSection = sortedSections[currentSectionIndex];
  const sectionItems = items?.filter(i => i.section_id === currentSection?.id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) || [];

  const totalItems = items?.length || 0;
  const answeredItems = Object.keys(localResponses).filter(k => localResponses[k].value !== null).length;
  const progress = totalItems > 0 ? (answeredItems / totalItems) * 100 : 0;

  const handleResponseChange = useCallback(async (itemId: string, value: string | null) => {
    setLocalResponses(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], value, notes: prev[itemId]?.notes || '', photoUrls: prev[itemId]?.photoUrls || [] }
    }));

    // Auto-save to database
    const existing = responses?.find(r => r.template_item_id === itemId);
    if (existing) {
      await updateResponse({ id: existing.id, response_value: value });
    } else {
      await createResponse({
        inspection_id: inspectionId,
        template_item_id: itemId,
        response_value: value
      });
    }
  }, [responses, inspectionId, createResponse, updateResponse]);

  const handleNotesChange = useCallback(async (itemId: string, notes: string) => {
    setLocalResponses(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], notes, value: prev[itemId]?.value || null, photoUrls: prev[itemId]?.photoUrls || [] }
    }));

    const existing = responses?.find(r => r.template_item_id === itemId);
    if (existing) {
      await updateResponse({ id: existing.id, notes });
    }
  }, [responses, updateResponse]);

  const handlePhotoUpload = async (itemId: string, file: File) => {
    try {
      const siteId = inspection?.site_id;
      if (!siteId) {
        toast.error('Inspection has no site assignment — cannot upload photo');
        return;
      }
      const fileExt = file.name.split('.').pop();
      const fileName = `${siteId}/${inspectionId}/${itemId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('inspection_photos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const signedUrl = await getSignedStorageUrl('inspection_photos', fileName);

      const currentPhotos = localResponses[itemId]?.photoUrls || [];
      const newPhotos = [...currentPhotos, signedUrl];

      setLocalResponses(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], photoUrls: newPhotos }
      }));

      const existing = responses?.find(r => r.template_item_id === itemId);
      if (existing) {
        await updateResponse({ id: existing.id, photo_urls: newPhotos });
      }

      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const validateResponses = (): string[] => {
    const errors: string[] = [];
    
    items?.forEach(item => {
      const response = localResponses[item.id];
      const isFail = response?.value === 'FAIL' || response?.value === 'NO';

      if (item.notes_required_if_fail && isFail && !response?.notes?.trim()) {
        errors.push(`"${item.item_text}" requires notes when failed`);
      }

      if (item.photo_required_if_fail && isFail && (!response?.photoUrls || response.photoUrls.length === 0)) {
        errors.push(`"${item.item_text}" requires a photo when failed`);
      }

      if (item.always_require_notes && !response?.notes?.trim()) {
        errors.push(`"${item.item_text}" always requires notes`);
      }
    });

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateResponses();
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateInspection({
        id: inspectionId,
        status: 'submitted',
        submitted_at: new Date().toISOString()
      });
      toast.success('Inspection submitted successfully');
      onSubmit();
    } catch (error) {
      toast.error('Failed to submit inspection');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderResponseControl = (item: any) => {
    const response = localResponses[item.id];
    const currentValue = response?.value;

    if (item.response_type === 'PASS_FAIL_NA') {
      return (
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={currentValue === 'PASS' ? 'default' : 'outline'}
            className={`h-7 px-2 ${currentValue === 'PASS' ? 'bg-green-600 hover:bg-green-700' : ''}`}
            onClick={() => handleResponseChange(item.id, 'PASS')}
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant={currentValue === 'FAIL' ? 'default' : 'outline'}
            className={`h-7 px-2 ${currentValue === 'FAIL' ? 'bg-destructive hover:bg-destructive/90' : ''}`}
            onClick={() => handleResponseChange(item.id, 'FAIL')}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant={currentValue === 'NA' ? 'default' : 'outline'}
            className={`h-7 px-2 ${currentValue === 'NA' ? 'bg-muted-foreground hover:bg-muted-foreground/90' : ''}`}
            onClick={() => handleResponseChange(item.id, 'NA')}
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
        </div>
      );
    }

    if (item.response_type === 'YES_NO') {
      return (
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={currentValue === 'YES' ? 'default' : 'outline'}
            className={`h-7 px-2 ${currentValue === 'YES' ? 'bg-green-600 hover:bg-green-700' : ''}`}
            onClick={() => handleResponseChange(item.id, 'YES')}
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant={currentValue === 'NO' ? 'default' : 'outline'}
            className={`h-7 px-2 ${currentValue === 'NO' ? 'bg-destructive hover:bg-destructive/90' : ''}`}
            onClick={() => handleResponseChange(item.id, 'NO')}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      );
    }

    if (item.response_type === 'TEXT') {
      return (
        <Input
          value={currentValue || ''}
          onChange={(e) => handleResponseChange(item.id, e.target.value)}
          placeholder="Enter..."
          className="h-7 text-xs w-32"
        />
      );
    }

    if (item.response_type === 'NUMBER') {
      return (
        <Input
          type="number"
          value={currentValue || ''}
          onChange={(e) => handleResponseChange(item.id, e.target.value)}
          placeholder="#"
          className="h-7 text-xs w-20"
        />
      );
    }

    if (item.response_type === 'DROPDOWN' && Array.isArray(item.dropdown_options)) {
      return (
        <div className="flex gap-1 flex-wrap">
          {item.dropdown_options.map((option: string) => (
            <Button
              key={option}
              size="sm"
              variant={currentValue === option ? 'default' : 'outline'}
              className="h-7 px-2 text-xs"
              onClick={() => handleResponseChange(item.id, option)}
            >
              {option}
            </Button>
          ))}
        </div>
      );
    }

    return null;
  };

  if (inspectionLoading) {
    return <div className="flex items-center justify-center p-8">Loading inspection...</div>;
  }

  if (inspectionError) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Unable to load inspection
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{error?.message || 'Unknown error'}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onBack}>Back</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <CardTitle>Inspection not found</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">This inspection may have been deleted or you may not have access to it.</p>
            <Button variant="outline" onClick={onBack}>Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header - Compact */}
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Exit
        </Button>
        <div className="text-center flex-1 min-w-0">
          <h2 className="text-sm font-semibold truncate">{inspection.template?.name}</h2>
          <p className="text-xs text-muted-foreground truncate">{inspection.site?.name} {inspection.area && `- ${inspection.area}`}</p>
        </div>
        <Badge variant="outline" className="text-xs">Draft</Badge>
      </div>

      {/* Progress - Compact */}
      <div className="flex items-center gap-2 text-xs">
        <Progress value={progress} className="h-1.5 flex-1" />
        <span className="text-muted-foreground whitespace-nowrap">{answeredItems}/{totalItems}</span>
      </div>

      {/* Section Navigation - Compact tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {sortedSections.map((section, index) => (
          <Button
            key={section.id}
            variant={index === currentSectionIndex ? 'default' : 'ghost'}
            size="sm"
            className="h-7 text-xs px-2 shrink-0"
            onClick={() => setCurrentSectionIndex(index)}
          >
            {section.name}
          </Button>
        ))}
      </div>

      {/* Current Section Items - Compact */}
      {currentSection && (
        <Card className="overflow-hidden">
          <CardHeader className="py-2 px-3 bg-muted/50">
            <CardTitle className="text-sm flex items-center gap-1">
              {currentSection.name}
              {currentSection.subsection && (
                <span className="text-muted-foreground font-normal text-xs">/ {currentSection.subsection}</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y">
            {sectionItems.map((item, index) => {
              const response = localResponses[item.id];
              const isFail = response?.value === 'FAIL' || response?.value === 'NO';
              const hasNotes = response?.notes && response.notes.length > 0;
              const hasPhotos = response?.photoUrls && response.photoUrls.length > 0;
              const needsNotes = item.always_require_notes || (item.notes_required_if_fail && isFail);
              const needsPhotos = item.photo_required_if_fail && isFail;

              return (
                <div 
                  key={item.id} 
                  className={`p-2 ${isFail ? 'bg-destructive/5' : ''}`}
                >
                  {/* Main row: code + text + response */}
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-mono text-muted-foreground shrink-0 w-12 pt-0.5">
                      {item.item_code || `${index + 1}`}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-tight">{item.item_text}</p>
                    </div>
                    <div className="shrink-0">
                      {renderResponseControl(item)}
                    </div>
                  </div>

                  {/* Expandable notes/photos row - only show if needed or has content */}
                  {(needsNotes || needsPhotos || hasNotes || hasPhotos) && (
                    <div className="mt-2 ml-14 flex gap-2 items-start">
                      <div className="flex-1">
                        <Textarea
                          value={response?.notes || ''}
                          onChange={(e) => handleNotesChange(item.id, e.target.value)}
                          placeholder={needsNotes ? "Notes required..." : "Notes (optional)"}
                          className={`text-xs min-h-[28px] h-7 resize-none ${needsNotes && !hasNotes ? 'border-destructive' : ''}`}
                          rows={1}
                        />
                      </div>
                      <div className="flex gap-1 items-center shrink-0">
                        {response?.photoUrls?.map((url, i) => (
                          <img key={i} src={url} alt="" className="w-7 h-7 object-cover rounded" />
                        ))}
                        <label className={`w-7 h-7 border rounded flex items-center justify-center cursor-pointer hover:bg-muted/50 ${needsPhotos && !hasPhotos ? 'border-destructive' : 'border-dashed'}`}>
                          <Camera className="h-3 w-3 text-muted-foreground" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handlePhotoUpload(item.id, e.target.files[0]);
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Validation Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {validationErrors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Navigation - Compact */}
      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          size="sm"
          disabled={currentSectionIndex === 0}
          onClick={() => setCurrentSectionIndex(i => i - 1)}
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Prev
        </Button>

        {currentSectionIndex === sortedSections.length - 1 ? (
          <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
            <Send className="h-3.5 w-3.5 mr-1" />
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        ) : (
          <Button size="sm" onClick={() => setCurrentSectionIndex(i => i + 1)}>
            Next
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};
